
import { useEffect, useRef, useState } from "react";

interface CreatableComboBoxProps {
    value: string | string[];
    onChange: (value: string | string[]) => void;
    options: string[];
    multiple?: boolean;
    placeholder?: string;
    disabled?: boolean;
    onCreateOption?: (newValue: string) => void;
}

export function CreatableComboBox({
value,
onChange,
options,
multiple = false,
placeholder = "Select or type to add...",
disabled = false,
onCreateOption,
}: CreatableComboBoxProps) {
const [searchTerm, setSearchTerm] = useState("");
const [isOpen, setIsOpen] = useState(false);
const containerRef = useRef<HTMLDivElement>(null);

// Filter options by search term
const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes(searchTerm.toLowerCase())
);

// Check if typed value is new (not in options)
const isNewValue = searchTerm.trim() && 
    !options.some(opt => opt.toLowerCase() === searchTerm.toLowerCase());

const handleSelect = (option: string) => {
    if (multiple) {
    const arr = Array.isArray(value) ? value : [];
    if (!arr.includes(option)) {
        onChange([...arr, option]);
    }
    } else {
    onChange(option);
    }
    setSearchTerm("");
    setIsOpen(false);
};

const handleCreate = () => {
    const newValue = searchTerm.trim();
    onCreateOption?.(newValue);  // Add to options list
    handleSelect(newValue);       // Select it
};

const handleRemove = (option: string) => {
    if (multiple && Array.isArray(value)) {
    onChange(value.filter(v => v !== option));
    }
};

// Click outside to close
useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
    }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

return (
    <div ref={containerRef} className="combobox">
    {/* Selected tags (multi-select) */}
    {multiple && Array.isArray(value) && value.length > 0 && (
        <div className="combobox__tags">
        {value.map(v => (
            <span key={v} className="combobox__tag">
            {v}
            <button type="button" onClick={() => handleRemove(v)}>×</button>
            </span>
        ))}
        </div>
    )}

    {/* Input */}
    <input
        type="text"
        className="combobox__input"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setIsOpen(true)}
        disabled={disabled}
    />

    {/* Dropdown */}
    {isOpen && (
        <div className="combobox__dropdown">
        {filteredOptions.map(option => (
            <button
            key={option}
            type="button"
            className="combobox__option"
            onClick={() => handleSelect(option)}
            >
            {option}
            </button>
        ))}
        
        {isNewValue && (
            <>
            <hr className="combobox__divider" />
            <button
                type="button"
                className="combobox__option combobox__option--create"
                onClick={handleCreate}
            >
                ➕ Create "{searchTerm}"
            </button>
            </>
        )}
        
        {filteredOptions.length === 0 && !isNewValue && (
            <div className="combobox__empty">No options found</div>
        )}
        </div>
    )}
    </div>
);
}
